import type { MapMarker } from "../../../../types/litigation";

/**
 * Angular radius applied to coincident markers (~78m at the equator).
 * Tight enough that the pins read as "same location, multiple
 * facilities", with a visible gap between them when zoomed in.
 */
const COINCIDENT_OFFSET_DEG = 0.0007;

/** Decimal precision at which two coordinates are treated as the same point. */
const COINCIDENT_BUCKET_DECIMALS = 5;

const bucketKey = (lat: number, lng: number): string =>
    `${lat.toFixed(COINCIDENT_BUCKET_DECIMALS)}|${lng.toFixed(COINCIDENT_BUCKET_DECIMALS)}`;

/**
 * Spread markers sharing the same (rounded) coordinates around their
 * shared centroid so each one stays individually visible and hoverable.
 * Pure and deterministic — same input, same output, regardless of array
 * order within a group.
 */
export function fanOutCoincidentMarkers(markers: MapMarker[]): MapMarker[] {
    const groups = new Map<string, number[]>();
    markers.forEach((m, idx) => {
        const key = bucketKey(m.lat, m.lng);
        const bucket = groups.get(key);
        if (bucket) bucket.push(idx);
        else groups.set(key, [idx]);
    });

    const out = markers.slice();
    groups.forEach((indexes) => {
        if (indexes.length < 2) return;
        const n = indexes.length;
        const centerLat = markers[indexes[0]].lat;
        // Compensate for longitude compression at higher latitudes so the
        // visual ring stays roughly circular instead of stretched east-west.
        const lngScale = 1 / Math.max(Math.cos((centerLat * Math.PI) / 180), 0.01);
        indexes.forEach((markerIdx, i) => {
            const angle = (2 * Math.PI * i) / n;
            const original = markers[markerIdx];
            out[markerIdx] = {
                ...original,
                lat: original.lat + COINCIDENT_OFFSET_DEG * Math.sin(angle),
                lng: original.lng + COINCIDENT_OFFSET_DEG * Math.cos(angle) * lngScale,
            };
        });
    });
    return out;
}
